import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles as SparkIcon, Download, Wand2, RotateCcw } from "lucide-react";
import Sparkles from "./Sparkles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const COLORS = [
  { name: "Red", value: "red", hex: "#E76F51" },
  { name: "Pink", value: "pink", hex: "#FFCBA4" },
  { name: "Purple", value: "purple", hex: "#9B5DE5" },
  { name: "Blue", value: "blue", hex: "#4EA8DE" },
  { name: "Green", value: "green", hex: "#52B788" },
  { name: "Yellow", value: "yellow", hex: "#FFD166" },
  { name: "Orange", value: "orange", hex: "#F4A261" },
  { name: "White", value: "white", hex: "#FDFBF7" },
  { name: "Gold", value: "gold", hex: "#FFD700" },
  { name: "Silver", value: "silver", hex: "#C0C0C0" },
  { name: "Teal", value: "teal", hex: "#2A9D8F" },
  { name: "Rainbow", value: "rainbow", hex: "linear-gradient(90deg,#E76F51,#FFD166,#52B788,#4EA8DE,#9B5DE5)" },
];

const PixieNameQuiz = () => {
  const [month, setMonth] = useState("");
  const [color, setColor] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!month || !color || !email) {
      toast.error("Pick a month, a color, and whisper your email!");
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/pixie-name`, {
        birth_month: month,
        favorite_color: color,
        email,
        subscribe: true,
      });
      setResult(res.data);
      toast.success("Your pixie name has been revealed! ✿");
    } catch (err) {
      console.error(err);
      toast.error("The fairy dust got muddled. Try again!");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setMonth("");
    setColor("");
    setEmail("");
  };

  return (
    <section
      id="pixie-quiz"
      data-testid="pixie-quiz-section"
      className="relative py-28 md:py-40 overflow-hidden"
      style={{ background: "linear-gradient(180deg,#0D2214 0%,#08140B 50%, #0D2214 100%)" }}
    >
      <Sparkles count={36} />
      <div className="relative max-w-5xl mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="font-accent text-xl text-amber-300 tracking-wide">
            A tiny enchantment
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mt-3">
            Find Your <span className="italic text-gradient-gold">Pixie Name</span>
          </h2>
          <div className="section-divider mt-6" />
          <p className="mt-6 text-emerald-50/75 max-w-xl mx-auto text-lg">
            Every child has a pixie name. Tell us a little about you, and we'll
            whisper yours into a free colouring page.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="glass-card p-7 md:p-10 relative"
        >
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-7"
                data-testid="pixie-quiz-form"
              >
                {/* Birth month */}
                <div>
                  <label className="block text-sm uppercase tracking-[0.2em] text-amber-300/90 mb-3">
                    1. Your birth month
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {MONTHS.map((m) => (
                      <button
                        type="button"
                        key={m}
                        onClick={() => setMonth(m)}
                        data-testid={`pixie-month-${m.toLowerCase()}`}
                        className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                          month === m
                            ? "bg-amber-400/20 border-amber-400/60 text-amber-200"
                            : "bg-emerald-950/40 border-amber-500/15 text-emerald-50/75 hover:border-amber-400/40 hover:text-amber-200"
                        }`}
                      >
                        {m.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm uppercase tracking-[0.2em] text-amber-300/90 mb-3">
                    2. Your favourite colour
                  </label>
                  <div className="flex flex-wrap gap-2.5">
                    {COLORS.map((c) => (
                      <button
                        type="button"
                        key={c.value}
                        onClick={() => setColor(c.value)}
                        data-testid={`pixie-color-${c.value}`}
                        title={c.name}
                        className={`group relative w-12 h-12 rounded-full border-2 transition-all ${
                          color === c.value
                            ? "border-amber-300 scale-110 ring-2 ring-amber-300/30"
                            : "border-amber-500/20 hover:border-amber-400/60 hover:scale-105"
                        }`}
                        style={{
                          background: c.hex.includes("gradient") ? c.hex : c.hex,
                        }}
                      >
                        <span className="sr-only">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm uppercase tracking-[0.2em] text-amber-300/90 mb-3">
                    3. Your email (for the colouring page)
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="parent@email.com"
                    data-testid="pixie-quiz-email"
                    className="w-full bg-emerald-950/60 border border-amber-500/20 focus:border-amber-400/60 rounded-xl px-4 py-3 text-white placeholder:text-emerald-50/40 focus:outline-none transition-colors"
                  />
                  <p className="text-xs text-emerald-50/55 mt-2">
                    We'll also send the moonbell newsletter (unsubscribe anytime).
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold w-full sm:w-auto justify-center disabled:opacity-60"
                  data-testid="pixie-quiz-submit"
                >
                  <Wand2 size={18} />
                  {submitting ? "Sprinkling stardust…" : "Reveal My Pixie Name"}
                </button>
              </motion.form>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center py-6"
                data-testid="pixie-quiz-result"
              >
                <SparkIcon className="text-amber-300 mx-auto mb-4 glow-pulse" size={38} />
                <div className="font-accent text-amber-300 text-xl">
                  Your pixie name is
                </div>
                <h3
                  className="font-heading text-5xl sm:text-6xl lg:text-7xl mt-3 text-gradient-gold italic"
                  data-testid="pixie-quiz-name"
                >
                  {result.pixie_name}
                </h3>
                <div className="font-accent text-amber-200/90 mt-4 text-lg">
                  {result.title}
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <a
                    href={result.coloring_page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="btn-gold"
                    data-testid="pixie-quiz-download"
                  >
                    <Download size={18} /> Free Colouring Page
                  </a>
                  <button onClick={reset} className="btn-ghost" data-testid="pixie-quiz-reset">
                    <RotateCcw size={16} /> Try Again
                  </button>
                </div>

                {result.already_subscribed ? (
                  <p className="text-xs text-emerald-50/55 mt-6">
                    You're already on the moonbell list — thank you!
                  </p>
                ) : (
                  <p className="text-xs text-emerald-50/55 mt-6">
                    ✿ Welcome to the moonbell list. A magical letter is on its way.
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default PixieNameQuiz;
